import React, { useEffect, useState } from "react";
import instance from "../../../../axios/instance";
import { getToken } from "../../../../utils/token";
import { useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import EarningsChart from "../components/EarningsChart";
import { FilterItem } from "../components/FilterItem";
import {
  ArrowLeft2,
  ArrowRight2,
  DocumentDownload,
  SearchNormal,
  Sort,
} from "iconsax-react";
import { PaginationButton } from "../../../../components/PaginationButton";
import dayjs from "dayjs";

export const customersEarningsLoader = async () => {
  const token = getToken();
  const customers = await instance.get("/doctor/connect/customers", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    customers: customers.data,
  };
};

export const CustomersEarningsPage = () => {
  const { customers } = useLoaderData();
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [_customers, setCustomers] = useState(
    [...customers].map((_, index) => ({
      ..._,
      index: index + 1,
    }))
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const navigate = useNavigate();

  const filterCustomers = (customers) => {
    setFilteredCustomers((items) =>
      customers.filter((_, index) => index + 1 <= 10)
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredCustomers(() => {
      return _customers.filter((customer) => {
        const id = `${customer.payer_id}` === query;
        const payer_name = customer.payer_name.toLowerCase().includes(query);

        return id || payer_name;
      });
    });
  };

  useEffect(() => {
    setFilteredCustomers(() =>
      _customers.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        _customers.reverse();
        setCustomers(_customers);
        filterCustomers(_customers);
        return;
      }
      case "name": {
        setPageIndex(1);
        if (sorted === "name") {
          setSorted("");
          _customers.sort((a, b) => a.index - b.index);
        } else {
          setSorted("name");
          _customers.sort((a, b) => {
            if (a.payer_name < b.payer_name) return -1;
            if (a.payer_name > b.payer_name) return 1;
            return 0;
          });
        }
        filterCustomers(_customers);
        return;
      }
      case "amount": {
        setPageIndex(1);
        if (sorted === "amount") {
          setSorted("");
          _customers.sort((a, b) => a.index - b.index);
        } else {
          setSorted("amount");
          _customers.sort((a, b) => {
            if (a.amount < b.amount) return -1;
            if (a.amount > b.amount) return 1;
            return 0;
          });
        }
        filterCustomers(_customers);
        return;
      }
    }
  };

  return (
    <div className="mt-5 max-h-[83vh] overflow-y-scroll">
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search customer"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(_customers.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("name")}
                >
                  Name{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "name" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("amount")}
                >
                  Total Spent{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "amount" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
              </tr>
            </thead>

            <tbody>
              {filteredCustomers.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No customers yet
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <tr
                    key={customer.payer_id}
                    className="h-14 text-[14px] border-b"
                  >
                    <td className="px-6 py-4">{customer.index}</td>
                    <td className="px-6 py-4">{customer.payer_id}</td>
                    <td className="px-6 py-4">{customer.payer_name}</td>
                    <td className="px-6 py-4">
                      ${customer.amount.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
